import React, { useEffect } from "react";
import Navigation from "../home/components/Navigation";
import Footer from "../home/components/Footer";

const AboutPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Lead Consultants of America
            </h1>
            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We're best known for lead, asbestos, mold, and indoor air quality work in housing portfolios, daycares and schools, commercial properties, and military communities—backed by the kind of documentation and data systems that stand up years later, not just at project closeout.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Founded in 1996, Lead Consultants of America, Inc. (LCA Group) began as a small lead and asbestos inspection firm helping local property owners navigate new regulations.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                As our clients' needs grew—from individual buildings to entire portfolios and P3/military housing programs—we saw a problem: the science was solid, but the systems were fragile. Paper forms, spreadsheets, and manual reporting couldn't keep up with thousands of units, tight schedules, and increasing scrutiny.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed font-semibold">
                So we did something different:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700">We standardized methods across bases, campuses, and portfolios.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700">We built web-based environmental data systems so owners could see risk at the unit, building, and program level.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700">We turned those tools into a broader platform (now called erblue) that connects field data, labs, and reporting.</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Today, that same mindset runs through everything we do: <strong>deep field experience + defensible evidence + modern workflows.</strong>
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                That's why organizations like Balfour Beatty Communities, federal agencies, transportation authorities, and school systems—along with thousands of homeowners and daycares—trust LCA to help keep their properties safe and compliant.
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/5172ee4ae609eaabc6423cf42abf6995.jpg"
                alt="LCA Group team at work"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Stand For</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Safety First</h3>
              <p className="text-gray-700 leading-relaxed">
                We never treat compliance as a checkbox. Our work is designed to protect the people in the building—families, tenants, staff, and workers.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-message-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Clear, Honest Communication</h3>
              <p className="text-gray-700 leading-relaxed">
                We translate complex environmental issues into plain language and practical choices, so you always know what a finding really means.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-shield-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Evidence You Can Rely On</h3>
              <p className="text-gray-700 leading-relaxed">
                From sampling plans to final reports, we design every step to be traceable, explainable, and defensible—for lenders, regulators, boards, and, if needed, attorneys.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Partnership Over the Long Term</h3>
              <p className="text-gray-700 leading-relaxed">
                We work like an extension of your team, not a one-off vendor—especially on long-running housing, school, and portfolio programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Credentials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Certifications & Credentials</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team maintains high industry standards and keeps pace with evolving regulations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">EPA-Certified</h3>
              <p className="text-gray-600 text-sm">Lead & Asbestos Inspectors</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">State-Licensed</h3>
              <p className="text-gray-600 text-sm">NJ, NY, PA, and DE</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">AHERA-Accredited</h3>
              <p className="text-gray-600 text-sm">School Asbestos Programs</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-microscope-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">AIHA-Affiliated</h3>
              <p className="text-gray-600 text-sm">Indoor Air Quality Experts</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl shadow-2xl p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Whether you're a homeowner, daycare or school, property manager, or portfolio owner, we can help you see the full picture and move forward with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact#contact-form"
                className="inline-block px-8 py-4 bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get in Touch
              </a>
              <a
                href="/services"
                className="inline-block px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
              >
                View Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
